import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { TableUrlPagination } from '../../../../ui'
import { TableActions } from '../table-actions'
import { installerDataPreparation } from '../../logics'
import { INSTALLER_TABLE_ACTIONS, INSTALLER_TABLE_COLUMNS } from '../../constants'
import { installerTableStyles } from './installer-table.styles'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const InstallerTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const installerLoader = useSelector((state) => state.loader.installers)
  const combos = useSelector((state) => state.combos)

  const data = installerLoader?.data?.rows || []
  const totalEntries = installerLoader?.data?.total_rows
  const search = installerLoader?.config?.params
  const loading = installerLoader?.loading

  const formattedColumns = useMemo(
    () =>
      [...INSTALLER_TABLE_COLUMNS, ...INSTALLER_TABLE_ACTIONS].map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.users.table.columns.${column.field}.title` }),
          size: column.width,
          minSize: column.width,
          maxSize: column.width,
        }

        if (column.field === 'actions') {
          return {
            ...baseColumn,
            header: formatMessage({ id: 'global.actions' }),
            Cell: (props) => {
              const { row } = props
              const backgroundColor =
                row?.original?.oauth2_id && !row?.original?.confirmed
                  ? installerTableStyles.failure.backgroundColor
                  : installerTableStyles.success.backgroundColor

              return <TableActions {...props} backgroundColor={backgroundColor} />
            },
            muiTableBodyCellProps: {
              align: 'center',
              sx: { padding: 0 },
            },
            muiTableHeadCellProps: { align: 'center' },
          }
        }

        if (column.comboId && combos[column.comboId]?.data) {
          const lookup = getLookup(combos[column.comboId].data)
          return {
            ...baseColumn,
            Cell: ({ cell }) => {
              const value = cell.getValue()
              const displayValue = lookup[value] ?? value ?? '--'

              if (
                (column.field === 'nombre' || column.field === 'apellidos') &&
                displayValue !== '--'
              ) {
                return (
                  <span>
                    {typeof displayValue === 'string' ? displayValue.toUpperCase() : displayValue}
                  </span>
                )
              }

              return <span>{displayValue}</span>
            },
            muiTableBodyCellProps: { sx: { padding: '0 16px' } },
          }
        }

        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue() ?? '--'

            if ((column.field === 'nombre' || column.field === 'apellidos') && value !== '--') {
              return <span>{typeof value === 'string' ? value.toUpperCase() : value}</span>
            }

            return <span>{value}</span>
          },
          muiTableBodyCellProps: { sx: { padding: '0 16px' } },
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(() => installerDataPreparation(data, { intl }), [data, intl])

  const finalTableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      draggable: false,
      fixedColumns: {
        right: 1,
      },
    }),
    []
  )

  const muiTableBodyRowProps = useMemo(() => {
    return ({ row }) => {
      const backgroundColor =
        row?.original?.oauth2_id && !row?.original?.confirmed
          ? installerTableStyles.failure.backgroundColor
          : installerTableStyles.success.backgroundColor

      return {
        sx: {
          backgroundColor,
          '& td': {
            backgroundColor: `${backgroundColor} !important`,
          },
        },
      }
    }
  }, [])

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={finalTableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      muiTableBodyRowProps={muiTableBodyRowProps}
    />
  )
}
